<script setup>
import { onMounted, ref, computed } from 'vue'
import { api } from '../../services/api'
import { useToast, VaCard, VaCardContent, VaButton, VaSelect } from 'vuestic-ui'
import { useCompanyStore } from '../../stores/companies'
import { useAppStore } from '../../stores/appStore'
import TenantCustomerDataTable from '../../components/reports/TenantCustomerDataTable.vue'

const { init: notify } = useToast()

const companyStore = useCompanyStore()
const appStore = useAppStore()

const companiesOptions = computed(() => {
  if (!Array.isArray(companyStore.companies)) {
    return []
  }
  return companyStore.companies.map((company) => ({
    text: `${company.name} ${!company.active ? '(Inativo)' : ''}`,
    value: company.id,
  }))
})

const filters = ref({
  tenant_id: null,
})

const reportData = ref({
  customers: [],
  counts: {
    titular_count: 0,
    dependent_count: 0,
  },
})

const isDownloading = ref(false)

async function downloadReport() {
  if (!filters.value.tenant_id) return

  isDownloading.value = true
  try {
    const response = await api.get('/reports/tenant-customers/export', {
      params: { tenant_id: filters.value.tenant_id },
      responseType: 'blob',
    })

    const contentDisposition = response.headers['content-disposition']
    let fileName = 'relatorio-empresa.xlsx'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) {
        fileName = match[1].replace(/['"]/g, '')
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error(error)
    notify({
      color: 'danger',
      message: 'Erro ao exportar relatório.',
    })
  } finally {
    isDownloading.value = false
  }
}

async function generateReport() {
  if (!filters.value.tenant_id) {
    notify({
      color: 'warning',
      message: 'Por favor, selecione uma empresa.',
    })
    return
  }

  appStore.toggleLoading(true)
  try {
    const response = await api.get('/reports/tenant-customers', {
      params: {
        tenant_id: filters.value.tenant_id,
      },
    })

    notify({
      color: 'success',
      message: 'Relatório gerado com sucesso!',
    })

    reportData.value = response.data.data
  } catch (error) {
    console.error(error)
    notify({
      color: 'danger',
      message: 'Erro ao gerar relatório.',
    })
  } finally {
    appStore.toggleLoading(false)
  }
}

onMounted(async () => {
  await companyStore.getCompanies(true)
})
</script>

<template>
  <div>
    <div class="flex items-center gap-4 flex-wrap mb-4">
      <h1 class="page-title !mb-0">Relatório de Usuários por Empresa</h1>
    </div>

    <VaCard class="mb-4">
      <VaCardContent>
        <h5 class="font-bold text-xl mb-4">Filtros</h5>
        <form class="grid grid-cols-1 md:grid-cols-3 items-end gap-4" @submit.prevent="generateReport">
          <div>
            <VaSelect
              v-model="filters.tenant_id"
              label="Empresa"
              track-by="text"
              value-by="value"
              :options="companiesOptions"
              searchable
              highlight-matched-text
              clearable
            />
          </div>
          <div>
            <VaButton :disabled="appStore.loading" type="submit" color="primary" class="w-full">
              <span v-if="appStore.loading">Gerando...</span>
              <span v-else>Gerar Relatório</span>
            </VaButton>
          </div>
        </form>
      </VaCardContent>
    </VaCard>

    <div v-if="reportData.customers.length" class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
      <VaCard>
        <VaCardContent>
          <p class="text-secondary uppercase font-bold text-xs">Total de Titulares</p>
          <h2 class="text-2xl font-bold text-primary">{{ reportData.counts.titular_count }}</h2>
        </VaCardContent>
      </VaCard>
      <VaCard>
        <VaCardContent>
          <p class="text-secondary uppercase font-bold text-xs">Total de Dependentes</p>
          <h2 class="text-2xl font-bold text-primary">{{ reportData.counts.dependent_count }}</h2>
        </VaCardContent>
      </VaCard>
      <VaCard>
        <VaCardContent>
          <p class="text-secondary uppercase font-bold text-xs">Plano Corporativo</p>
          <h2 class="text-2xl font-bold text-primary">{{ reportData.counts.can_have_dependents_count }}</h2>
        </VaCardContent>
      </VaCard>
      <VaCard>
        <VaCardContent>
          <p class="text-secondary uppercase font-bold text-xs">Plano Básico</p>
          <h2 class="text-2xl font-bold text-primary">{{ reportData.counts.cannot_have_dependents_count }}</h2>
        </VaCardContent>
      </VaCard>
    </div>

    <VaCard v-if="reportData.customers.length">
      <VaCardContent>
        <div class="flex justify-end mb-4">
          <VaButton :disabled="isDownloading" color="success" icon="download" @click="downloadReport">
            <span v-if="isDownloading">Exportando...</span>
            <span v-else>Exportar Excel</span>
          </VaButton>
        </div>
        <TenantCustomerDataTable :data="reportData.customers" class="mt-6" />
      </VaCardContent>
    </VaCard>

    <div v-else-if="!appStore.loading && filters.tenant_id" class="mt-6 text-center">
      <VaCard>
        <VaCardContent>
          <p class="text-secondary">Nenhum dado encontrado para esta empresa.</p>
        </VaCardContent>
      </VaCard>
    </div>
  </div>
</template>

<style scoped></style>
